import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';
import ToggleSwitch from './ToggleSwitch';
import CloseIcon from '../icons/CloseIcon';
import { format } from 'date-fns';

interface StudentRoleInfoProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
  onExpireToggle?: (isExpired: boolean) => void;
  studentName: string;
  studentNumber: number;
  roleName: string;
  position: string; // '부장' | '조원'
  assignedAt: Date;
  salary: number;
  salaryType: 'point' | 'stamp';
  salaryPeriod: 'daily' | 'weekly' | 'monthly';
  pointAutoPay: boolean;
  isExpired?: boolean;
}

const StudentRoleInfo: React.FC<StudentRoleInfoProps> = ({
  isOpen,
  onClose,
  onComplete,
  onExpireToggle,
  studentName,
  studentNumber,
  roleName,
  position,
  assignedAt,
  salary,
  salaryType,
  salaryPeriod,
  pointAutoPay,
  isExpired = false
}) => {
  const [expired, setExpired] = useState(isExpired);

  // 다이얼로그가 열릴 때 body 스크롤 방지
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setExpired(isExpired);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isExpired]);

  if (!isOpen) return null;

  // 역할 수행 기간 계산
  const getRoleDuration = (assignedAt: Date) => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - assignedAt.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // 급여 지급 주기 텍스트
  const getSalaryPeriodText = (period: 'daily' | 'weekly' | 'monthly') => {
    switch (period) {
      case 'daily':
        return '매일 지급';
      case 'weekly':
        return '매주 금요일 지급';
      case 'monthly':
        return '매월 1일 지급';
      default:
        return '';
    }
  };

  // 급여 타입 텍스트
  const getSalaryTypeText = (type: 'point' | 'stamp') => {
    return type === 'point' ? '포인트' : '도장';
  };

  // 급여 주기 라벨 (일급/주급/월급)
  const getSalaryPeriodLabel = (period: 'daily' | 'weekly' | 'monthly') => {
    switch (period) {
      case 'daily':
        return '일급';
      case 'weekly':
        return '주급';
      case 'monthly':
        return '월급';
      default:
        return '';
    }
  };

  const duration = getRoleDuration(assignedAt);
  const salaryText = getSalaryTypeText(salaryType);
  const periodText = getSalaryPeriodText(salaryPeriod);
  const periodLabel = getSalaryPeriodLabel(salaryPeriod);

  const handleExpireToggle = (checked: boolean) => {
    setExpired(checked);
    if (onExpireToggle) {
      onExpireToggle(checked);
    }
  };

  // Portal을 사용하여 body에 직접 렌더링 (레이아웃 transform 영향 회피)
  return createPortal(
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 99999,
        overflow: 'hidden',
        pointerEvents: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={(e) => {
        // 배경 클릭 시 닫기
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        style={{
          width: '450px',
          maxWidth: 'calc(100vw - 40px)',
          maxHeight: 'calc(100vh - 40px)',
          background: 'white',
          borderRadius: '16px',
          padding: '24px 28px',
          display: 'flex',
          flexDirection: 'column',
          gap: '40px',
          overflow: 'auto',
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '8px'
        }}>
          <div style={{
            flex: '1 1 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px'
          }}>
            <div style={{
              color: '#161616',
              fontSize: '20px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              lineHeight: '30px'
            }}>
              학생 역할 정보
            </div>
          </div>
          <div
            onClick={onClose}
            style={{
              padding: '4px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CloseIcon width={24} height={24} />
          </div>
        </div>

        {/* 정보 영역 */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px'
        }}>
          {/* 학생 이름, 번호 */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: '24px'
          }}>
            <div style={{
              width: '152px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <div style={{
                color: '#989898',
                fontSize: '14px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: '21px'
              }}>
                학생 이름
              </div>
              <div style={{
                color: '#161616',
                fontSize: '16px',
                fontFamily: 'Pretendard',
                fontWeight: '500',
                lineHeight: '24px'
              }}>
                {studentName}
              </div>
            </div>
            <div style={{
              width: '185px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <div style={{
                color: '#989898',
                fontSize: '14px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: '21px'
              }}>
                번호
              </div>
              <div style={{
                color: '#161616',
                fontSize: '16px',
                fontFamily: 'Pretendard',
                fontWeight: '500',
                lineHeight: '24px'
              }}>
                {studentNumber}번
              </div>
            </div>
          </div>

          {/* 역할(직급), 역할 수행기간 */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '24px'
          }}>
            <div style={{
              width: '152px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <div style={{
                color: '#989898',
                fontSize: '14px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: '21px'
              }}>
                역할 (직급)
              </div>
              <div style={{
                color: '#161616',
                fontSize: '16px',
                fontFamily: 'Pretendard',
                fontWeight: '500',
                lineHeight: '24px'
              }}>
                {roleName} ({position})
              </div>
            </div>
            <div style={{
              width: '218px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <div style={{
                color: '#989898',
                fontSize: '14px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: '21px'
              }}>
                역할 수행기간
              </div>
              <div style={{
                color: '#161616',
                fontSize: '16px',
                fontFamily: 'Pretendard',
                fontWeight: '500',
                lineHeight: '24px'
              }}>
                {duration}일 ({format(assignedAt, 'yyyy.MM.dd')}~)
              </div>
            </div>
          </div>

          {/* 급여, 포인트 자동 지급 여부 */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '24px'
          }}>
            <div style={{
              width: '152px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <div style={{
                color: '#989898',
                fontSize: '14px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: '21px'
              }}>
                급여
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: '4px'
              }}>
                <div style={{
                  padding: '2px 12px',
                  background: '#FFD2B5',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    color: '#FF6F0F',
                    fontSize: '12px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: '18px'
                  }}>
                    {periodLabel}
                  </div>
                </div>
                <div style={{
                  color: '#161616',
                  fontSize: '16px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  lineHeight: '24px'
                }}>
                  {salary} {salaryText}
                </div>
              </div>
            </div>
            <div style={{
              width: '250px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <div style={{
                color: '#989898',
                fontSize: '14px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: '21px'
              }}>
                포인트 자동 지급 여부
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: '4px',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  padding: '2px 12px',
                  background: pointAutoPay ? '#F5F5F5' : '#F8F8F8',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <div style={{
                    color: '#33AFD7',
                    fontSize: '12px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: '18px',
                    whiteSpace: 'nowrap'
                  }}>
                    {pointAutoPay ? '자동' : '수동'}
                  </div>
                </div>
                {pointAutoPay && (
                  <div style={{
                    color: '#161616',
                    fontSize: '16px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: '24px',
                    whiteSpace: 'nowrap'
                  }}>
                    {periodText}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 역할 수행 만료 처리 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            <div style={{
              color: '#989898',
              fontSize: '14px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              lineHeight: '21px'
            }}>
              역할 수행 만료 처리
            </div>
            <ToggleSwitch
              checked={expired}
              onChange={handleExpireToggle}
              size="medium"
            />
          </div>
        </div>

        {/* 완료 버튼 */}
        {onComplete && (
          <div style={{
            width: '100%',
            height: '45px'
          }}>
            <Button
              variant="submit"
              onClick={onComplete}
              width="100%"
              height="100%"
            >
              완료
            </Button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default StudentRoleInfo;
